/**
 * TalentProfileHeader molecule — identity block for the talent detail header.
 *
 * Renders Avatar (lg), full name (h3), email/title/location (body-sm),
 * total YoE summary, top-3 SkillChip display, and the primary "Assign to job" CTA.
 * Profile fields are edited inline per-section (no separate "Edit profile" button).
 *
 * i18n keys: talentPool.detail.assignToJob
 * All copy via i18next talentPool namespace. No hardcoded strings.
 * No gradients, no emojis — light brand only.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, UserPlus, Trash2, Download } from 'lucide-react';
import { Avatar } from '@/components/ui/atoms/Avatar';
import { Typography } from '@/components/ui/atoms/Typography';
import { SkillChip } from '@/components/ui/atoms/SkillChip';
import { Button } from '@/components/ui/atoms/Button';
import type { TalentProfile } from '@/types/talent.types';

export interface TalentProfileHeaderProps {
  profile: TalentProfile;
  onAssignClick: () => void;
  onBackClick: () => void;
  onDeleteClick: () => void;
  onDownloadResume: () => void;
  isDownloadingResume?: boolean;
}

export const TalentProfileHeader: React.FC<TalentProfileHeaderProps> = ({
  profile,
  onAssignClick,
  onBackClick,
  onDeleteClick,
  onDownloadResume,
  isDownloadingResume = false,
}) => {
  const { t } = useTranslation('talentPool');

  const initials = profile.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const topSkills = profile.technicalSkills.slice(0, 3);
  const yoeDisplay = profile.totalYoe >= 1
    ? `${Math.floor(profile.totalYoe)} ${t('pool.yoeSuffix')}`
    : `< 1 ${t('pool.yoeSuffix')}`;

  return (
    <div className="flex items-start gap-4 p-6 bg-light border-b border-light-lila/40">
      {/* Back arrow — returns to the talent pool list (mirrors Job Details header). */}
      <Button
        type="icon"
        intent="muted"
        onClick={onBackClick}
        aria-label={t('detail.backToPool')}
        title={t('detail.backToPool')}
        icon={<ChevronLeft />}
        className="shrink-0 mt-1"
      />

      <Avatar
        initials={initials}
        src={profile.avatarUrl}
        alt={profile.fullName}
        size="lg"
        variant="neutral"
      />

      <div className="flex-1 min-w-0">
        <Typography variant="h3" weight="semibold" color="primary">
          {profile.fullName}
        </Typography>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
          <Typography variant="body-sm" color="secondary">
            {profile.email}
          </Typography>
          {/* phone — TAL-06: displayed raw, no formatting (D-13) */}
          {profile.phone && (
            <>
              <span className="text-light-lila" aria-hidden="true">·</span>
              <Typography variant="body-sm" color="secondary">
                {profile.phone}
              </Typography>
            </>
          )}
          {profile.title && (
            <>
              <span className="text-light-lila" aria-hidden="true">·</span>
              <Typography variant="body-sm" color="secondary">
                {profile.title}
              </Typography>
            </>
          )}
          {profile.location && (
            <>
              <span className="text-light-lila" aria-hidden="true">·</span>
              <Typography variant="body-sm" color="secondary">
                {profile.location}
              </Typography>
            </>
          )}
          {profile.totalYoe > 0 && (
            <>
              <span className="text-light-lila" aria-hidden="true">·</span>
              <Typography variant="body-sm" color="secondary">
                {yoeDisplay}
              </Typography>
            </>
          )}
        </div>

        {topSkills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {topSkills.map((skill) => (
              <SkillChip key={skill.name} label={skill.name} tone="info" />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {/* Top-level primary CTA — labelled "Assign to job" with a leading icon. */}
        <Button
          type="contained"
          intent="secondary"
          icon={<UserPlus/>}
          label={t('detail.assignToJob')}
          onClick={onAssignClick}
        />
        {/* Download résumé — subtle icon-only action, mirrors the header's other
            icon buttons (back arrow / delete). Spinner while generating. */}
        <Button
          type="icon"
          intent="muted"
          onClick={onDownloadResume}
          disabled={isDownloadingResume}
          loading={isDownloadingResume}
          aria-label={t('detail.iconTitles.downloadResume')}
          title={t('detail.iconTitles.downloadResume')}
          icon={<Download />}
        />
        {/* Destructive action — icon-only; opens a confirm dialog before deleting. */}
        <Button
          type="icon"
          intent="danger"
          onClick={onDeleteClick}
          aria-label={t('detail.deleteTalent')}
          title={t('detail.deleteTalent')}
          icon={<Trash2 />}
        />
      </div>
    </div>
  );
};

TalentProfileHeader.displayName = 'TalentProfileHeader';
